import { ApiService, TApiServiceError, TApiServiceOptions } from "../apiService";

import { handleAsync } from "@/utils";
import { TApiResponse } from "../../types/api";
import { withAuthHooks } from "../auth/auth.ky.hooks";
import type {
  TFeedback,
  TFeedbackAnalytics,
  TFeedbackList,
  TFeedbackListParams,
  TFeedbackSubmitPayload,
} from "./feedback.types";

const apiService = new ApiService({
  hooks: {
    ...withAuthHooks,
  },
});

class FeedbackService {
  private rootEndpoint = "feedback" as const;
  private adminEndpoint = "admin/feedback" as const;
  private uploadEndpoint = "files/upload" as const;

  submitFeedback(payload: TFeedbackSubmitPayload, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TFeedback>>(() =>
      apiService.post(`${this.rootEndpoint}/`, payload, options)
    );
  }

  /** Screenshot upload step — rides the shared files pipeline as a
   * `feedback_attachment` (image-only, capped server-side). */
  uploadScreenshot(file: File, options?: TApiServiceOptions) {
    const formData = new FormData();
    formData.append("file", file);
    return handleAsync<TApiServiceError, TApiResponse<{ id: string }>>(() =>
      apiService.post(`${this.uploadEndpoint}?file_type=feedback_attachment`, formData, options)
    );
  }

  getMyFeedback(limit = 20, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TFeedbackList>>(() =>
      apiService.get(`${this.rootEndpoint}/me?limit=${limit}`, options)
    );
  }

  listFeedback(params: TFeedbackListParams = {}, options?: TApiServiceOptions) {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
    }
    const qs = search.toString();
    return handleAsync<TApiServiceError, TApiResponse<TFeedbackList>>(() =>
      apiService.get(`${this.adminEndpoint}/${qs ? `?${qs}` : ""}`, options)
    );
  }

  getFeedbackAnalytics(windowDays = 30, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TFeedbackAnalytics>>(() =>
      apiService.get(`${this.adminEndpoint}/analytics?window_days=${windowDays}`, options)
    );
  }

  getUserFeedback(userId: string, limit = 50, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TFeedbackList>>(() =>
      apiService.get(`${this.adminEndpoint}/users/${userId}?limit=${limit}`, options)
    );
  }

  markReviewed(feedbackId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TFeedback>>(() =>
      apiService.patch(`${this.adminEndpoint}/${feedbackId}/review`, undefined, options)
    );
  }
}

export const feedbackService = new FeedbackService();
